import React, { useEffect } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { toRelativeUrl } from "@okta/okta-auth-js";
import { useSearchParams } from "react-router-dom";
import { Loader } from "@components/Loader";
import { isStoryblokEditor } from "@utils/storyblokEditor";
import { EditorPreviewLogin } from "@components/EditorPreviewLogin";

export const SecureRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [searchParams] = useSearchParams();
  const isEditor = isStoryblokEditor(searchParams);

  // Inside the Storyblok Visual Editor the page loads in a third-party iframe that cannot
  // complete an Okta redirect login. EditorPreviewLogin authenticates these sessions via a
  // top-level popup handoff instead, then renders the previewed page. The API still requires
  // the resulting Okta bearer on draft requests, so the `_storyblok_tk` token alone no longer
  // grants access.
  if (isEditor) {
    return <EditorPreviewLogin>{children}</EditorPreviewLogin>;
  }

  return <SecureRouteWithOkta>{children}</SecureRouteWithOkta>;
};

const SecureRouteWithOkta: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { oktaAuth, authState } = useOktaAuth();
  const isAuthenticated = authState?.isAuthenticated;
  useEffect(() => {
    if (!authState) {
      return;
    }

    if (!isAuthenticated) {
      const originalUri = toRelativeUrl(
        window.location.href,
        window.location.origin
      );
      oktaAuth.setOriginalUri(originalUri);
      void oktaAuth.signInWithRedirect();
    }
  }, [oktaAuth, authState, isAuthenticated]);
  if (!authState?.isAuthenticated) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return <>{children}</>;
};
